import type {ReactNode} from 'react';
import type {Assignment, PermissionDefinition, PermissionGrant, ProfileHead, Scope, StaffAccess} from './types';
import {catalogueRows, groupPermissionRows, isAdmin, scopeLabel, statusLabel} from './utils';

export function Notice({tone = 'info', children}: {tone?: 'info' | 'warning' | 'danger' | 'success'; children: ReactNode}) {
  return <div className={`notice ${tone}`} role={tone === 'danger' ? 'alert' : 'status'}>{children}</div>;
}

export function Spinner({label = 'Chargement…'}: {label?: string}) {
  return <div className="spinner" role="status"><span />{label}</div>;
}

export function Empty({children}: {children: ReactNode}) {
  return <div className="empty">{children}</div>;
}

export function ScopeBadge({scope}: {scope: Scope}) {
  return <span className="chip">{scopeLabel(scope)}</span>;
}

export function StatusChip({status}: {status: string}) {
  const kind = status === 'active' || status === 'suspended' ? status : 'none';
  return <span className={`chip st-${kind}`}>{statusLabel(status)}</span>;
}
export function AdminNotice({active = true}: {active?: boolean}) {
  return <Notice tone={active ? 'info' : 'warning'}><strong>Admin — tous les droits actuels et futurs.</strong>{' '}
    {active ? 'Tous les périmètres compatibles, avec délégation complète.' : 'Statut configuré, accès actuellement inactif.'}</Notice>;
}

/** Plafond de délégation d'un aperçu : permission + périmètre, sans catalogue à charger. */
export function GrantCards({grants}: {grants: PermissionGrant[]}) {
  if (!grants.length) return <p className="muted small">Aucun plafond de délégation.</p>;
  return <ul className="cards">{grants.map((grant, index) => <li key={`${grant.permission}-${index}`}>
    <strong><code>{grant.permission}</code></strong><span className="sub">{scopeLabel(grant.scope)}</span>
  </li>)}</ul>;
}

export function AccessSnapshot({value, title}: {value: StaffAccess | null; title: string}) {
  return <section className="snapshot">
    <div className="section-title"><h4>{title}</h4>{value && <><StatusChip status={value.status} /><span>révision {value.revision}</span></>}</div>
    {!value ? <Empty>Aucun accès staff.</Empty> : <>
      {isAdmin(value) && <AdminNotice active={value.status === 'active'} />}
      {!isAdmin(value) && <p className="muted small">Compte sans statut admin. Droits définis par ses affectations.</p>}
      <h5>Affectations</h5>
      {value.assignments.length ? <ul className="cards">{value.assignments.map(item => <li key={item.assignmentId}>
        <strong>{item.profileId} v{item.profileVersion}</strong><span className="sub">{scopeLabel(item.scope)} · id {item.assignmentId}</span>
      </li>)}</ul> : <p className="muted small">Aucune affectation.</p>}
      <h5>Plafond de délégation</h5>
      <GrantCards grants={value.delegation} />
    </>}
  </section>;
}

export function ScopeEditor({scope, onChange}: {scope: Scope; onChange(scope: Scope): void}) {
  return <div className="scope-editor">
    <select aria-label="Type de périmètre" value={scope.kind} onChange={event => {
      const kind = event.target.value as Scope['kind'];
      if (kind === 'global') onChange({kind});
      else if (kind === 'project') onChange({kind, projectId: ''});
      else if (kind === 'branch') onChange({kind, projectId: '', branchId: ''});
      else onChange({kind, channel: 'intercom'});
    }}>
      <option value="global">Tous les périmètres</option><option value="project">Projet</option><option value="branch">Branche</option><option value="supportChannel">Canal support</option>
    </select>
    {(scope.kind === 'project' || scope.kind === 'branch') && <input aria-label="ID du projet" placeholder="ID du projet" value={scope.projectId} onChange={event => onChange({...scope, projectId: event.target.value})} required />}
    {scope.kind === 'branch' && <input aria-label="ID de la branche" placeholder="ID de la branche" value={scope.branchId} onChange={event => onChange({...scope, branchId: event.target.value})} required />}
    {scope.kind === 'supportChannel' && <select aria-label="Canal support" value={scope.channel} onChange={event => onChange({...scope, channel: event.target.value as 'intercom' | 'discord'})}><option value="intercom">Intercom</option><option value="discord">Discord</option></select>}
  </div>;
}

/** Versions proposables d'un profil : celles que la liste connaît, la dernière en tête de choix. */
export function knownVersions(latest: number, current: number): number[] {
  const versions = new Set<number>();
  for (let version = Math.max(1, latest); version >= 1; version -= 1) versions.add(version);
  if (current >= 1) versions.add(current);
  return [...versions].sort((a, b) => b - a);
}

/**
 * Le profil se choisit par son nom, son identifiant et sa dernière version : plus de saisie
 * libre d'un identifiant ou d'un numéro de version qui n'existe pas.
 */
export function AssignmentRow({value, onChange, onRemove, profiles}: {value: Assignment; onChange(value: Assignment): void; onRemove(): void; profiles: ProfileHead[]}) {
  const known = profiles.find(profile => profile.profileId === value.profileId);
  const options = known || !value.profileId ? profiles : [...profiles, {id: value.profileId, profileId: value.profileId, name: value.profileId, latestVersion: value.profileVersion}];
  return <div className="editor-row">
    <label>Profil
      <select value={value.profileId} onChange={event => {
        const next = profiles.find(profile => profile.profileId === event.target.value);
        onChange({...value, profileId: event.target.value, profileVersion: next?.latestVersion || value.profileVersion});
      }} required>
        {!value.profileId && <option value="">Choisir un profil</option>}
        {options.map(profile => <option key={profile.profileId} value={profile.profileId}>{profile.name} · {profile.profileId} · v{profile.latestVersion}</option>)}
      </select>
    </label>
    <label>Version
      <select value={value.profileVersion} onChange={event => onChange({...value, profileVersion: Number(event.target.value)})} required>
        {knownVersions(known?.latestVersion || value.profileVersion, value.profileVersion).map(version => <option key={version} value={version}>
          v{version}{version === (known?.latestVersion || value.profileVersion) ? ' (dernière)' : ''}
        </option>)}
      </select>
    </label>
    <ScopeEditor scope={value.scope} onChange={scope => onChange({...value, scope})} />
    <span className="muted small">id {value.assignmentId}</span>
    <button type="button" className="danger-link" onClick={onRemove}>Retirer</button>
  </div>;
}

/** Les permissions du plafond sont regroupées par outil : une liste plate ne se lit pas. */
export function DelegationRow({value, onChange, onRemove, definitions}: {value: PermissionGrant; onChange(value: PermissionGrant): void; onRemove(): void; definitions: PermissionDefinition[]}) {
  const groups = groupPermissionRows(catalogueRows(definitions));
  return <div className="editor-row delegation-row">
    <label>Permission
      <select value={value.permission} onChange={event => onChange({...value, permission: event.target.value})}>
        {groups.map(group => <optgroup key={group.module} label={group.label}>
          {group.rows.map(row => <option key={row.permission} value={row.permission}>{row.permission} — {row.description}</option>)}
        </optgroup>)}
      </select>
    </label>
    <ScopeEditor scope={value.scope} onChange={scope => onChange({...value, scope})} />
    <button type="button" className="danger-link" onClick={onRemove}>Retirer</button>
  </div>;
}
